/* eslint-disable no-console */
// Redux Saga
import { takeLatest, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Services
import { garageApi } from 'services/apiDigitalTicket/garage';

// Contants
import { errorMessagersList } from 'helpers/errorMessagers';
import PATH from 'constants/basePath';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';
import * as GarageActions from './actions';

// Types
import { GarageTypes, GarageInterface } from './types';

function* list({
  payload,
}: ReturnType<typeof GarageActions.listGarageRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(
        DialogsActions.setOpenLoading({ title: 'Carregando garagens' }),
      );
    }
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(garageApi.list, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        GarageActions.listGarageSuccess({
          garageList: response.data.map((garage: GarageInterface) => ({
            ...garage,
            responsible: garage?.TransportSupervisor?.name ?? '',
          })),
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );

      if (!filtering) {
        yield put(DialogsActions.setClose());
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('garage - listGarage - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(GarageActions.listGarageFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof GarageActions.getGarageRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando garagem' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(garageApi.list, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        GarageActions.getGarageSuccess({
          garageData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('garage - getGarage - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(GarageActions.getGarageFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
}: ReturnType<typeof GarageActions.filterGarageRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(garageApi.list, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        GarageActions.filterGarageSuccess({
          garageList: response.data.map((garage: GarageInterface) => ({
            ...garage,
            responsible: garage.TransportSupervisor.name,
          })),
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('garage - filterGarage - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(GarageActions.filterGarageFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
}: ReturnType<typeof GarageActions.createGarageRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando garagem' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(garageApi.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        GarageActions.createGarageSuccess({
          garageData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Garagem criada com sucesso',
        }),
      );
      yield put(
        GarageActions.listGarageRequest({
          body: {
            _includes: 'responsible,services,quantity,cars,address',
          },
        }),
      );
      yield put(push(PATH.GARAGE.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('garage - createGarage - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(GarageActions.createGarageFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: any | ReturnType<typeof GarageActions.updateGarageRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(garageApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        GarageActions.updateGarageSuccess({
          garageData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Garagem alterada com sucesso',
        }),
      );
      yield put(
        GarageActions.getGarageRequest({
          id: payload.garageId,
          _includes: 'responsible,services,quantity,cars,address',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('garage - updateGarage - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(GarageActions.updateGarageFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof GarageActions.deleteGarageRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo garagem' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(garageApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Garagem excluída com sucesso',
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        GarageActions.listGarageRequest({
          body: {
            _includes: 'responsible,services,quantity,cars,address',
          },
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('garage - deleteGarage - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(GarageActions.deleteGarageFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const garageSagas = [
  takeLatest(GarageTypes.LIST_GARAGE_REQUEST, list),
  takeLatest(GarageTypes.GET_GARAGE_REQUEST, get),
  takeLatest(GarageTypes.FILTER_GARAGE_REQUEST, filter),
  takeLatest(GarageTypes.CREATE_GARAGE_REQUEST, create),
  takeLatest(GarageTypes.UPDATE_GARAGE_REQUEST, update),
  takeLatest(GarageTypes.DELETE_GARAGE_REQUEST, remove),
];
